import { Navigate } from "react-router";
import { type UserRole, useUserRoles, hasRole } from "@/lib/auth/roles";
import { db } from "@/lib/instantdb";
import { ShieldX } from "lucide-react";

interface RoleGuardProps {
  /** Required roles to access the content (any match grants access) */
  requiredRoles: UserRole[];
  /** Content to render if access is granted */
  children: React.ReactNode;
  /** Where to redirect if access is denied (defaults to showing access denied page) */
  redirectTo?: string;
  /** If true, show access denied page instead of redirecting (prevents redirect loops) */
  showAccessDenied?: boolean;
}

/**
 * Access Denied component - shown when user doesn't have required role
 */
function AccessDenied({ requiredRoles }: { requiredRoles: UserRole[] }) {
  const { user } = db.useAuth();

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <ShieldX className="h-16 w-16 text-destructive" />
      <h1 className="text-2xl font-bold">Access Denied</h1>
      <p className="max-w-md text-muted-foreground">
        You don't have permission to access this page. Required role:{" "}
        <strong>{requiredRoles.join(" or ")}</strong>
      </p>
      {user?.email && (
        <p className="text-sm text-muted-foreground">
          Logged in as: {user.email}
        </p>
      )}
      <button
        onClick={() => db.auth.signOut()}
        className="mt-4 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
      >
        Sign Out
      </button>
    </div>
  );
}

/**
 * RoleGuard protects routes based on user roles
 *
 * Usage:
 * ```tsx
 * <RoleGuard requiredRoles={['admin']}>
 *   <AdminPanel />
 * </RoleGuard>
 * ```
 */
export function RoleGuard({
  requiredRoles,
  children,
  redirectTo,
  showAccessDenied = false,
}: RoleGuardProps) {
  const { roles, isLoading } = useUserRoles();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Check role access
  if (!hasRole(roles, requiredRoles)) {
    // Show access denied page instead of redirect to prevent loops
    if (showAccessDenied || !redirectTo) {
      return <AccessDenied requiredRoles={requiredRoles} />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * AdminGuard - shorthand for admin-only content
 */
export function AdminGuard({
  children,
  redirectTo,
}: Omit<RoleGuardProps, "requiredRoles">) {
  return (
    <RoleGuard requiredRoles={["admin"]} redirectTo={redirectTo}>
      {children}
    </RoleGuard>
  );
}

/**
 * AnalystGuard - for analytics access (admin or analyst roles)
 */
export function AnalystGuard({
  children,
  redirectTo,
}: Omit<RoleGuardProps, "requiredRoles">) {
  return (
    <RoleGuard requiredRoles={["admin", "analyst"]} redirectTo={redirectTo}>
      {children}
    </RoleGuard>
  );
}

/**
 * useIsAdmin hook - for conditional rendering
 */
export function useIsAdmin(): boolean {
  const { isAdmin } = useUserRoles();
  return isAdmin;
}

/**
 * useIsMobile hook - always false for admin web app
 */
export function useIsMobile(): boolean {
  return false;
}

export default RoleGuard;
